/**
 * Generates a proper sequential token number
 * Format: [StudioCode]-[DDMMYY]-[SequentialPosition]-[CheckDigit]
 * Example: 13-011225-001-7 (BLR13, Dec 1 2025, Position 1)
 * 
 * FIX #3: Uses bookingDate parameter instead of always using today's date
 */
export const generateSequentialToken = (siteCode, sequentialPosition, bookingDate = null) => {
  // Use the booking date, not the current date
  const targetDate = bookingDate ? new Date(bookingDate) : new Date();
  const day = targetDate.getDate().toString().padStart(2, '0');
  const month = (targetDate.getMonth() + 1).toString().padStart(2, '0');
  const year = targetDate.getFullYear().toString().slice(-2);
  
  // Extract numeric part from site code (BLR13 -> 13)
  const sitePrefix = siteCode.replace('BLR', '');
  
  // Pad position to 3 digits
  const paddedPosition = sequentialPosition.toString().padStart(3, '0');
  
  // Generate check digit (simple sum modulo 10)
  const baseToken = `${sitePrefix}${day}${month}${year}${paddedPosition}`;
  const checkDigit = baseToken.split('').reduce((sum, digit) => sum + parseInt(digit), 0) % 10;
  
  return `${sitePrefix}-${day}${month}${year}-${paddedPosition}-${checkDigit}`;
};

/**
 * Validates a token format
 * Format: [StudioCode]-[DDMMYY]-[Position]-[CheckDigit]
 * Example: 13-011225-001-7
 */
export const validateToken = (token) => {
  // Updated pattern to match actual generation format
  const tokenPattern = /^\d{2}-\d{6}-\d{3}-\d$/;
  
  if (!tokenPattern.test(token)) {
    return false;
  }
  
  // Validate check digit
  const parts = token.split('-');
  if (parts.length !== 4) return false;
  
  const baseToken = parts[0] + parts[1] + parts[2];
  const expectedCheckDigit = baseToken.split('').reduce((sum, digit) => sum + parseInt(digit), 0) % 10;
  
  return parseInt(parts[3]) === expectedCheckDigit;
};

/**
 * Calculates estimated arrival time based on queue position
 * Includes realistic timing factors: 2.25min base + 30s setup + 15s buffer = 3min per person
 * 
 * FIX #3: Uses bookingDate parameter instead of always using today
 * FIX #4: Properly handles lunch break by adding remaining wait time AFTER the break
 */
export const calculateEstimatedTime = (queuePosition, slotStartTime = null, bookingDate = null) => {
  // Use booking date, not current date
  const targetDate = bookingDate ? new Date(bookingDate) : new Date();
  const now = new Date();
  
  let startTime;
  if (slotStartTime) {
    const [startHour, startMinute] = slotStartTime.split(':').map(Number);
    startTime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), startHour, startMinute);
  } else {
    const isToday = targetDate.toDateString() === now.toDateString();
    if (isToday) {
      const currentHour = now.getHours();
      if (currentHour < 10) {
        startTime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 10, 0);
      } else if (currentHour >= 14 && currentHour < 15) {
        startTime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 15, 0);
      } else {
        startTime = now;
      }
    } else {
      // Future date — start from 10 AM
      startTime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 10, 0);
    }
  }
  
  // 3 minutes (180 seconds) per person
  const totalWaitSeconds = (queuePosition - 1) * 180;
  
  // Calculate arrival time, properly skipping lunch break (2-3 PM)
  const lunchStart = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 14, 0);
  const lunchEnd = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 15, 0);
  const lunchDurationMs = lunchEnd - lunchStart; // 1 hour in ms
  
  let arrivalTime = new Date(startTime.getTime() + totalWaitSeconds * 1000);
  
  // If arrival time falls during or after lunch, push it forward by the lunch duration
  if (startTime < lunchStart && arrivalTime >= lunchStart) {
    // The wait crosses the lunch break — add the full lunch hour
    arrivalTime = new Date(arrivalTime.getTime() + lunchDurationMs);
  }
  
  // For today's bookings: if calculated arrival is in the past, bump to now + 1 min
  const isToday = targetDate.toDateString() === now.toDateString();
  if (isToday && arrivalTime < now) {
    arrivalTime = new Date(now.getTime() + 60000); // now + 1 minute
  }
  
  // Ensure we don't go past 5 PM
  const closingTime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 17, 0);
  if (arrivalTime > closingTime) {
    throw new Error('Queue is too long. Estimated time would be after 5 PM closing.');
  }
  
  const hour = arrivalTime.getHours();
  const minute = arrivalTime.getMinutes();
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
  
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};